/**
 * International geo redirect pop-up for the US storefront (jlobeauty.com).
 *
 * Catches international visitors who land on the US store from ANY channel
 * (organic, direct, social, paid). Tagged traffic is NOT suppressed.
 * Uses Shopify IP geolocation — no third-party geo API.
 *
 * Flow:
 *   0. Every page load: save entry data (UTMs, referrer domain, landing path)
 *      in sessionStorage 'jlo_entry' so it can be passed to the global site.
 *   1. Skip bots, theme editor, global subdomain, saved cookie
 *      (saved "international" preference → tagged redirect, us_trigger=return_visit)
 *   2. Detect country via browsing_context_suggestions.json
 *   3. US → do nothing (Canada sees the pop-up)
 *   4. Supported country (50-country launch list) → supported pop-up → global
 *   5. Unsupported country → Coming soon pop-up (Instagram + stay on US)
 *   6. Save choice in jlo_region_preference cookie (30 days)
 *
 * Redirect URL carries (never any user/customer identifier):
 *   utm_source / utm_medium / utm_campaign / utm_content / utm_term / utm_id
 *     → original values; fallback referrer domain / referral / intl_popup,
 *       or jlobeauty_us / geo_redirect / intl_popup when no referrer
 *   intl_ref=us_geo_popup, us_country, us_trigger, us_variant,
 *   us_landing, us_page, us_ref
 *
 * Console: verbose logs only when config.debug is true (myshopify.com staging).
 * Production logs errors only.
 *
 * Staging QA (myshopify.com):
 *   ?jlo_geo_test=GB&jlo_geo_test_name=United%20Kingdom   → supported pop-up
 *   ?jlo_geo_test=IN&jlo_geo_test_name=India                 → Coming soon pop-up
 *   ?jlo_geo_clear=1
 */
(function () {
  'use strict';

  var LOG = '[JLO Geo]';
  var config = window.JLO_GEO_CONFIG;
  var popupIsVisible = false;
  var shouldSuppressKlaviyo = false;
  var detectedCountry = null;
  var activeVariant = null;
  var popupTimer = null;
  var ENTRY_SESSION_KEY = 'jlo_entry';
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];

  if (!config || !config.enabled) return;

  // Run immediately on every page so UTMs are saved before the visitor browses away.
  captureEntryData();

  // Register before async geo fetch — Klaviyo can fire early.
  installKlaviyoSuppression();

  whenDomReady(function () {
    if (!bindPopupEvents()) return;
    runGeoFlow();
  });

  // -------------------------------------------------------------------------
  // Main flow
  // -------------------------------------------------------------------------

  function runGeoFlow() {
    if (clearQaCookieIfRequested()) return;

    if (config.isDesignMode) return;

    if (window.location.hostname.indexOf('global.') === 0) return;

    // SEO: never show pop-up or redirect crawlers.
    if (isBot()) return;

    // Return visitors: honour 30-day preference cookie.
    var preference = getCookie(config.cookieName);
    if (preference === 'international') {
      redirectToGlobal('cookie', 'return_visit');
      return;
    }
    if (preference === 'us') return;

    // No UTM suppression: tagged traffic (paid, social, email) sees the pop-up too.

    // Staging only: ?jlo_geo_test=GB skips IP lookup.
    var qaCountry = getQaOverrideCountry();
    if (qaCountry) {
      debugWarn('QA override active:', qaCountry);
      handleDetectedCountry(qaCountry);
      return;
    }

    detectCountry()
      .then(function (country) {
        // Fail closed: no country from Shopify → no pop-up.
        if (!country || !country.isoCode) return;
        handleDetectedCountry(country);
      })
      .catch(function (err) {
        console.error(LOG, 'Country detection failed.', err);
      });
  }

  /**
   * Catch-all routing:
   *   US                          → no pop-up
   *   Supported launch markets    → supported pop-up (Yes → global)
   *   Everyone else               → Coming soon pop-up (Instagram + stay on US)
   */
  function handleDetectedCountry(country) {
    detectedCountry = country;
    debugLog('Country:', country.isoCode, country.name);

    if (isDomestic(country.isoCode)) {
      debugLog('Domestic visitor. No pop-up.');
      return;
    }

    var variant = resolvePopupVariant(country.isoCode);
    if (!variant) {
      debugLog('Unsupported country and Coming soon pop-up disabled. No pop-up.');
      return;
    }

    shouldSuppressKlaviyo = true;
    activeVariant = variant;

    trackEvent('jlo_geo_popup_scheduled', {
      country_code: country.isoCode,
      country_name: country.name,
      variant: variant
    });

    popupTimer = setTimeout(function () {
      showPopup(country, variant);
    }, config.displayDelayMs);
  }

  function resolvePopupVariant(isoCode) {
    if (isSupportedCountry(isoCode)) return 'supported';
    if (config.enableUnsupportedPopup) return 'unsupported';
    return null;
  }

  function isSupportedCountry(isoCode) {
    var list = config.supportedCountries || [];
    return list.indexOf(isoCode) !== -1;
  }

  // -------------------------------------------------------------------------
  // IP detection (Shopify native)
  // -------------------------------------------------------------------------

  /**
   * Shopify reads visitor IP server-side and returns country in
   * browsing_context_suggestions.json. We do not fall back to Liquid
   * localization.country — on a US-only store that is usually "US" for everyone.
   */
  function detectCountry() {
    var url = countrySuggestionsUrl();

    return fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('browsing_context_suggestions HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        debugLog('Geo response:', data);

        var detected = data && data.detected_values && data.detected_values.country;
        if (detected && detected.handle) {
          return normalizeCountry(detected.handle, detected.name);
        }

        // Alternate response shape from Shopify.
        var suggestions = data && data.suggestions;
        if (suggestions && suggestions.length) {
          for (var i = 0; i < suggestions.length; i++) {
            var part = suggestions[i].parts && suggestions[i].parts.country;
            if (part && part.handle) {
              return normalizeCountry(part.handle, part.name);
            }
          }
        }

        debugWarn('No country in Shopify geo response. Failing closed.');
        return null;
      });
  }

  function countrySuggestionsUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'browsing_context_suggestions.json';
  }

  function normalizeCountry(isoCode, name) {
    return {
      isoCode: String(isoCode).toUpperCase(),
      name: name || isoCode
    };
  }

  function isDomestic(isoCode) {
    return config.domesticCountries.indexOf(isoCode) !== -1;
  }

  // -------------------------------------------------------------------------
  // Pop-up UI
  // -------------------------------------------------------------------------

  function bindPopupEvents() {
    var overlay = document.getElementById('jlo-geo-popup-overlay');
    if (!overlay) {
      console.error(LOG, 'Pop-up markup not found.');
      return false;
    }

    overlay.addEventListener('click', function (e) {
      // Backdrop click does not dismiss — user must choose explicitly.
      if (e.target === overlay) return;

      var action = e.target.closest('[data-jlo-geo-action]');
      if (!action) return;

      if (action.dataset.jloGeoAction === 'accept') handleAccept();
      if (action.dataset.jloGeoAction === 'instagram') handleInstagram(e);
      if (action.dataset.jloGeoAction === 'dismiss') {
        handleDismiss(action.classList.contains('jlo-geo-popup__close') ? 'close' : 'link');
      }
    });

    // Escape = same as "stay on US site" (sets cookie).
    document.addEventListener('keydown', function (e) {
      if (!popupIsVisible || e.key !== 'Escape') return;
      handleDismiss('escape');
    });

    return true;
  }

  function showPopup(country, variant) {
    if (popupIsVisible) return;

    var overlay = document.getElementById('jlo-geo-popup-overlay');
    if (!overlay) return;

    var supportedPanel = overlay.querySelector('[data-jlo-geo-variant="supported"]');
    var unsupportedPanel = overlay.querySelector('[data-jlo-geo-variant="unsupported"]');

    if (supportedPanel) supportedPanel.hidden = variant !== 'supported';
    if (unsupportedPanel) unsupportedPanel.hidden = variant !== 'unsupported';

    // Point aria-labelledby at the visible headline.
    overlay.setAttribute(
      'aria-labelledby',
      variant === 'unsupported' ? 'jlo-geo-popup-headline-unsupported' : 'jlo-geo-popup-headline'
    );

    // Country name only applies to the supported body copy.
    if (variant === 'supported') {
      var nameEls = overlay.querySelectorAll('[data-jlo-geo-country-name]');
      for (var i = 0; i < nameEls.length; i++) {
        nameEls[i].textContent = country.name;
      }
    }

    overlay.removeAttribute('hidden');
    overlay.classList.add('is-visible');
    document.body.classList.add('jlo-geo-popup-open');
    popupIsVisible = true;

    trackEvent('jlo_geo_popup_shown', {
      country_code: country.isoCode,
      country_name: country.name,
      variant: variant
    });
  }

  function hidePopup() {
    var overlay = document.getElementById('jlo-geo-popup-overlay');
    if (!overlay) return;

    if (popupTimer) {
      clearTimeout(popupTimer);
      popupTimer = null;
    }

    overlay.classList.remove('is-visible');
    overlay.setAttribute('hidden', '');
    document.body.classList.remove('jlo-geo-popup-open');
    popupIsVisible = false;
    activeVariant = null;
  }

  function handleAccept() {
    // Only the supported pop-up offers Yes → global.
    if (activeVariant !== 'supported') return;

    setPreferenceCookie('international');
    hidePopup();

    trackEvent('jlo_geo_popup_accept', {
      country_code: detectedCountry && detectedCountry.isoCode,
      country_name: detectedCountry && detectedCountry.name,
      variant: 'supported'
    });

    redirectToGlobal('popup_accept', 'geo_auto');
  }

  /**
   * Unsupported pop-up primary CTA: open Instagram in a new tab,
   * remember "stay on US" so the pop-up does not return for 30 days.
   */
  function handleInstagram(e) {
    setPreferenceCookie('us');

    trackEvent('jlo_geo_popup_instagram', {
      country_code: detectedCountry && detectedCountry.isoCode,
      country_name: detectedCountry && detectedCountry.name,
      variant: 'unsupported'
    });

    hidePopup();
    // Let the default <a target="_blank"> navigation proceed.
  }

  function handleDismiss(source) {
    setPreferenceCookie('us');
    hidePopup();

    trackEvent('jlo_geo_popup_dismiss', {
      source: source,
      country_code: detectedCountry && detectedCountry.isoCode,
      country_name: detectedCountry && detectedCountry.name,
      variant: activeVariant
    });
  }

  // -------------------------------------------------------------------------
  // Klaviyo suppression
  // -------------------------------------------------------------------------

  function installKlaviyoSuppression() {
    window.addEventListener('klaviyoForms', function (e) {
      if (!shouldSuppressKlaviyo || !e.detail || e.detail.type !== 'open') return;

      if (window.klaviyo && typeof window.klaviyo.closeForm === 'function') {
        window.klaviyo.closeForm(e.detail.formId);
      }
    });
  }

  // -------------------------------------------------------------------------
  // Redirect + cookies
  // -------------------------------------------------------------------------

  function redirectToGlobal(reason, trigger) {
    var base = (config.globalUrl || '').trim();

    if (!/^https?:\/\//i.test(base)) {
      console.error(LOG, 'Invalid globalUrl. Aborting redirect.');
      return;
    }

    var url = buildGlobalUrl(base, trigger);
    debugLog('Redirect URL:', url);

    trackEvent('jlo_geo_redirect', { reason: reason, destination: url });
    window.location.assign(url);
  }

  /**
   * Adds campaign + context parameters to the global store URL.
   * Campaign/page-level data only — never a GA client ID, _gl/_ga,
   * ad click IDs, Shopify customer/cart IDs or email.
   */
  // function buildGlobalUrl(base, trigger) {
  //   var url;
  //   try {
  //     url = new URL(base);
  //   } catch (e) {
  //     return base;
  //   }

  //   var p = url.searchParams;
  //   var entry = getEntryData();

  //   if (entry.utm_source) {
  //     // Original campaign values pass through unchanged.
  //     for (var i = 0; i < UTM_KEYS.length; i++) {
  //       if (entry[UTM_KEYS[i]]) p.set(UTM_KEYS[i], entry[UTM_KEYS[i]]);
  //     }
  //   } else if (entry.ref) {
  //     // Untagged but has an outside referrer (e.g. l.instagram.com).
  //     p.set('utm_source', entry.ref);
  //     p.set('utm_medium', 'referral');
  //     p.set('utm_campaign', 'intl_popup');
  //   } else {
  //     p.set('utm_source', 'jlobeauty_us');
  //     p.set('utm_medium', 'geo_redirect');
  //     p.set('utm_campaign', 'intl_popup');
  //   }

  //   p.set('intl_ref', 'us_geo_popup');
  //   if (detectedCountry && detectedCountry.isoCode) p.set('us_country', detectedCountry.isoCode);
  //   if (trigger) p.set('us_trigger', trigger);
  //   if (config.popupVersion) p.set('us_variant', config.popupVersion);
  //   if (entry.landing) p.set('us_landing', entry.landing);
  //   p.set('us_page', window.location.pathname);
  //   if (entry.ref) p.set('us_ref', entry.ref);

  //   return url.toString();
  // }

  function buildGlobalUrl(base, trigger) {
    var url;

    try {
      url = new URL(base);
    } catch (e) {
      return base;
    }

    var p = url.searchParams;
    var entry = getEntryData();

    /*
    * Carry ALL original query parameters from jlobeauty.com
    * to the global storefront.
    *
    * Exception:
    * Never transfer an email address through utm_source.
    */
    if (entry.params && typeof entry.params === 'object') {
      Object.keys(entry.params).forEach(function (key) {
        var value = entry.params[key];

        if (!value) return;

        if (
          key.toLowerCase() === 'utm_source' &&
          isEmailAddress(value)
        ) {
          return;
        }

        p.set(key, value);
      });
    }

    /*
    * Maintain your existing fallback attribution logic.
    *
    * We only generate a fallback utm_source when the original URL
    * doesn't contain a safe utm_source.
    */
    var safeUtmSource =
      entry.params &&
      entry.params.utm_source &&
      !isEmailAddress(entry.params.utm_source)
        ? entry.params.utm_source
        : '';

    if (!safeUtmSource) {
      if (entry.ref) {
        p.set('utm_source', entry.ref);

        if (!p.has('utm_medium')) {
          p.set('utm_medium', 'referral');
        }

        if (!p.has('utm_campaign')) {
          p.set('utm_campaign', 'intl_popup');
        }
      } else {
        /*
        * IMPORTANT:
        *
        * If you want utm_source COMPLETELY REMOVED when it contained
        * an email, comment out the following three fallback assignments.
        *
        * Otherwise this replaces:
        *
        * utm_source=test@email.com
        *
        * with:
        *
        * utm_source=jlobeauty_us
        */
        p.set('utm_source', 'jlobeauty_us');

        if (!p.has('utm_medium')) {
          p.set('utm_medium', 'geo_redirect');
        }

        if (!p.has('utm_campaign')) {
          p.set('utm_campaign', 'intl_popup');
        }
      }
    }

    // Existing geo tracking parameters.
    p.set('intl_ref', 'us_geo_popup');

    if (detectedCountry && detectedCountry.isoCode) {
      p.set('us_country', detectedCountry.isoCode);
    }

    if (trigger) {
      p.set('us_trigger', trigger);
    }

    if (config.popupVersion) {
      p.set('us_variant', config.popupVersion);
    }

    if (entry.landing) {
      p.set('us_landing', entry.landing);
    }

    p.set('us_page', window.location.pathname);

    if (entry.ref) {
      p.set('us_ref', entry.ref);
    }

    return url.toString();
  }

  // -------------------------------------------------------------------------
  // Entry data (sessionStorage, first-party, stays in this browser)
  // -------------------------------------------------------------------------

  /**
   * Saves on the first page of the session, and again whenever a page
   * arrives with a new utm_source (a new tagged click mid-session).
   * Referrer = hostname only. Landing = path only (no query string).
   */
  // function captureEntryData() {
  //   try {
  //     var params = new URLSearchParams(window.location.search);
  //     var hasNewUtm = params.has('utm_source');
  //     if (sessionStorage.getItem(ENTRY_SESSION_KEY) && !hasNewUtm) return;

  //     var entry = {};
  //     for (var i = 0; i < UTM_KEYS.length; i++) {
  //       var v = params.get(UTM_KEYS[i]);
  //       if (v) entry[UTM_KEYS[i]] = v;
  //     }

  //     var ref = '';
  //     if (document.referrer) {
  //       try {
  //         ref = new URL(document.referrer).hostname;
  //       } catch (_) {}
  //     }
  //     if (ref && ref !== window.location.hostname && !/(^|\.)jlobeauty\.com$/i.test(ref)) {
  //       entry.ref = ref;
  //     }

  //     entry.landing = window.location.pathname;
  //     sessionStorage.setItem(ENTRY_SESSION_KEY, JSON.stringify(entry));
  //     debugLog('Entry data saved:', entry);
  //   } catch (e) {
  //     // sessionStorage blocked: redirect falls back to default tags.
  //   }
  // }
  function captureEntryData() {
    try {
      var params = new URLSearchParams(window.location.search);

      // Consider any query-string landing as potentially new entry data.
      var hasQueryParams = Array.from(params.keys()).length > 0;

      // Keep the first entry unless another URL arrives with query params.
      if (sessionStorage.getItem(ENTRY_SESSION_KEY) && !hasQueryParams) {
        return;
      }

      var entry = {
        params: {}
      };

      /*
      * Save ALL incoming query parameters.
      *
      * Important:
      * Never store an email address coming through utm_source.
      */
      params.forEach(function (value, key) {
        if (!value) return;

        if (
          key.toLowerCase() === 'utm_source' &&
          isEmailAddress(value)
        ) {
          debugLog('Removed email-valued utm_source from redirect data.');
          return;
        }

        entry.params[key] = value;
      });

      // Keep these properties for compatibility with existing logic.
      for (var i = 0; i < UTM_KEYS.length; i++) {
        var key = UTM_KEYS[i];

        if (
          entry.params[key] &&
          !(key === 'utm_source' && isEmailAddress(entry.params[key]))
        ) {
          entry[key] = entry.params[key];
        }
      }

      var ref = '';

      if (document.referrer) {
        try {
          ref = new URL(document.referrer).hostname;
        } catch (_) {}
      }

      if (
        ref &&
        ref !== window.location.hostname &&
        !/(^|\.)jlobeauty\.com$/i.test(ref)
      ) {
        entry.ref = ref;
      }

      entry.landing = window.location.pathname;

      sessionStorage.setItem(
        ENTRY_SESSION_KEY,
        JSON.stringify(entry)
      );

      debugLog('Entry data saved:', entry);
    } catch (e) {
      // sessionStorage blocked: redirect falls back to default tags.
    }
  }

  function isEmailAddress(value) {
    if (!value) return false;

    var decodedValue = value;

    try {
      decodedValue = decodeURIComponent(value);
    } catch (_) {}

    decodedValue = String(decodedValue).trim();

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(decodedValue);
  }
  function getEntryData() {
    try {
      return JSON.parse(sessionStorage.getItem(ENTRY_SESSION_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  /**
   * jlo_region_preference values:
   *   "international" → auto-redirect to global on future visits
   *   "us"            → suppress pop-up for 30 days
   */
  function setPreferenceCookie(value) {
    if (value !== 'international' && value !== 'us') return; 
    setCookie(config.cookieName, value, config.cookieDays);
  }

  function setCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 864e5).toUTCString();
    var parts = [
      name + '=' + encodeURIComponent(value),
      'expires=' + expires,
      'path=/',
      'SameSite=Lax'
    ];

    var domain = getCookieDomain();
    if (domain) parts.push('domain=' + domain);
    if (window.location.protocol === 'https:') parts.push('Secure');

    document.cookie = parts.join('; ');
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  function getCookieDomain() {
    // Share preference across jlobeauty.com subdomains in production.
    if (window.location.hostname.endsWith('jlobeauty.com')) {
      return '.jlobeauty.com';
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // Exclusions
  // -------------------------------------------------------------------------

  // UTM suppression removed for JLO Beauty: international visitors see the
  // pop-up regardless of channel. UTMs are passed to the global site instead.

  function isBot() {
    var ua = navigator.userAgent || '';
    return /googlebot|bingbot|duckduckbot|bot|crawler|spider|scraper|shopify/i.test(ua);
  }

  // -------------------------------------------------------------------------
  // Staging QA helpers (gated by config.debug)
  // -------------------------------------------------------------------------

  function getQaOverrideCountry() {
    if (!config.debug) return null;

    try {
      var params = new URLSearchParams(window.location.search);
      var code = params.get('jlo_geo_test');
      if (!code) return null;
      return normalizeCountry(code, params.get('jlo_geo_test_name'));
    } catch (e) {
      return null;
    }
  }

  function clearQaCookieIfRequested() {
    if (!config.debug) return false;

    try {
      var params = new URLSearchParams(window.location.search);
      if (!params.has('jlo_geo_clear')) return false;

      setCookie(config.cookieName, '', -1);
      params.delete('jlo_geo_clear');
      var next = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.location.replace(next);
      return true;
    } catch (e) {
      return false;
    }
  }

  // -------------------------------------------------------------------------
  // Utilities
  // -------------------------------------------------------------------------
    var base = (config.globalUrl || '').trim();
    var url;
    try {
      url = new URL(base);
    } catch (e) {
      return base;
    }
    var entry = getEntryData();
    var p = url.searchParams;
    var originalUtmSource =
    entry.params && entry.params.utm_source
      ? entry.params.utm_source
      : '';

  var sourceWasEmail = isEmailAddress(originalUtmSource);

  if (!originalUtmSource) {
    // There was genuinely no utm_source.
    if (entry.ref) {
      p.set('utm_source', entry.ref);

      if (!p.has('utm_medium')) {
        p.set('utm_medium', 'referral');
      }

      if (!p.has('utm_campaign')) {
        p.set('utm_campaign', 'intl_popup');
      }
    } else {
      p.set('utm_source', 'jlobeauty_us');

      if (!p.has('utm_medium')) {
        p.set('utm_medium', 'geo_redirect');
      }

      if (!p.has('utm_campaign')) {
        p.set('utm_campaign', 'intl_popup');
      }
    }
  } else if (sourceWasEmail) {
    // Email existed as utm_source.
    // Explicitly remove it and DON'T replace it.
    p.delete('utm_source');
  }
  function whenDomReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function trackEvent(eventName, payload) {
    if (!window.dataLayer || !Array.isArray(window.dataLayer)) return;
    window.dataLayer.push(Object.assign({ event: eventName }, payload || {}));
  }

  // Staging-only console helpers — no output on jlobeauty.com production.
  function debugLog() {
    if (!config.debug) return;
    var args = [LOG].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }

  function debugWarn() {
    if (!config.debug) return;
    var args = [LOG].concat(Array.prototype.slice.call(arguments));
    console.warn.apply(console, args);
  }
})();

